import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMonthCursor } from '@/hooks/useMonthCursor'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { Plus, Pencil, Copy, Trash2, AlertCircle, ChevronLeft, ChevronRight, Repeat, ArrowLeftRight, ArrowRight, ArrowUp, ArrowDown, ChevronsUpDown, Download } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Card, CardContent } from '@/components/ui/card'
import { EmptyState } from '@/components/ui/empty-state'
import { TableSkeleton } from '@/components/ui/skeleton'
import { PageHeader } from '@/components/ui/page-header'
import { Input } from '@/components/ui/input'
import { MoneyInput } from '@/components/ui/money-input'
import { TagBadges } from '@/components/transactions/TagBadges'
import { DateInput } from '@/components/ui/date-input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  useTransactions,
  useCardMonthStatementTxs,
  useDeleteTransaction,
  useToggleStatus,
  useSaveTransaction,
  type TransactionRow,
  type TransactionInput,
} from '@/hooks/useTransactions'
import { useAccounts, useCardMonthFatura, useCardMonthCompras } from '@/hooks/useAccounts'
import { useTags } from '@/hooks/useTags'
import { FilterBar, EMPTY_FILTERS, activeCount, type Filters } from '@/components/filters/FilterBar'
import { matchTx, categoryScope } from '@/components/filters/applyFilters'
import { Combobox, type ComboOption } from '@/components/ui/combobox'
import { useCategories, buildTree, buildPicker } from '@/hooks/useCategories'
import { useHousehold } from '@/context/HouseholdContext'
import { useConfirm } from '@/context/ConfirmProvider'
import { formatCurrency } from '@/lib/currency'
import { accountIcon } from '@/lib/accountIcon'
import { accountComboOptions } from '@/lib/accountOptions'
import { ConversionIndicator } from '@/components/ConversionIndicator'
import { dueDateForMonth, dueISOForMonth } from '@/lib/cards'
import { cn } from '@/lib/utils'
import { TransactionFormDialog, defaultDateForMonth } from '@/components/transactions/TransactionFormDialog'
import { TransferDialog } from '@/components/transactions/TransferDialog'
import { IncludeRecurringDialog } from '@/components/recurring/IncludeRecurringDialog'
import { MonthPicker } from '@/components/ui/month-picker'
import { useT } from '@/i18n/I18nProvider'
import { useTranslateCategory } from '@/lib/categoryDisplay'

function monthLabel(d: Date) {
  return format(d, "MMMM 'de' yyyy", { locale: ptBR })
}
function isCurrentMonth(d: Date) {
  const n = new Date()
  return d.getFullYear() === n.getFullYear() && d.getMonth() === n.getMonth()
}
function monthBounds(d: Date) {
  const first = new Date(d.getFullYear(), d.getMonth(), 1)
  const last = new Date(d.getFullYear(), d.getMonth() + 1, 0)
  const iso = (x: Date) => x.toISOString().slice(0, 10)
  return { from: iso(first), to: iso(last) }
}

type PeriodMode = 'month' | '3m' | '6m' | '12m' | 'year' | 'all' | 'custom'

// Um par de transferência colapsado em UMA linha (as duas pernas viram uma só).
type TransferInfo = {
  id: string // transfer_group_id
  legId: string // id de uma perna (para excluir)
  date: string
  description: string | null
  status: TransactionRow['status']
  fromAcc: TransactionRow['account'] // conta de ORIGEM (perna de saída)
  toAcc: TransactionRow['account'] // conta de DESTINO (perna de entrada)
  fromAccId: string | null // id da conta de origem (para editar)
  toAccId: string | null // id da conta de destino (para editar)
  fromAmount: number
  fromCurrency: string
  fromBase: number | null
  toAmount: number
  toCurrency: string
}

export function TransactionsPage() {
  const { t: tt } = useT() // 't' já é usado como variável de TransactionRow neste arquivo
  const tc = useTranslateCategory()
  const { baseCurrency } = useHousehold()
  const navigate = useNavigate()
  const [cursor, setCursor] = useMonthCursor()
  const [period, setPeriod] = useState<PeriodMode>('month')
  const [customFrom, setCustomFrom] = useState('')
  const [customTo, setCustomTo] = useState('')
  // Faixa de datas conforme o período. 'month' = mês navegável (padrão); os demais
  // são relativos a hoje (últimos N meses / ano / tudo) ou a um intervalo escolhido.
  const range = useMemo(() => {
    if (period === 'month') return monthBounds(cursor)
    const isoL = (x: Date) =>
      `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`
    const now = new Date()
    const mEnd = (d: Date) => new Date(d.getFullYear(), d.getMonth() + 1, 0)
    const back = (n: number) => new Date(now.getFullYear(), now.getMonth() - n, 1)
    switch (period) {
      case '3m': return { from: isoL(back(2)), to: isoL(mEnd(now)) }
      case '6m': return { from: isoL(back(5)), to: isoL(mEnd(now)) }
      case '12m': return { from: isoL(back(11)), to: isoL(mEnd(now)) }
      case 'year': return { from: `${now.getFullYear()}-01-01`, to: `${now.getFullYear()}-12-31` }
      case 'all': return {} as { from?: string; to?: string }
      case 'custom': return { from: customFrom || undefined, to: customTo || undefined }
      default: return monthBounds(cursor)
    }
  }, [period, cursor, customFrom, customTo])
  const periodLabel = useMemo(() => {
    switch (period) {
      case 'month': return monthLabel(cursor)
      case '3m': return tt('tx.last3Label')
      case '6m': return tt('tx.last6Label')
      case '12m': return tt('tx.last12Label')
      case 'year': return String(new Date().getFullYear())
      case 'all': return tt('tx.allLabel')
      case 'custom': return customFrom || customTo ? `${customFrom || '…'} → ${customTo || '…'}` : tt('tx.customLabel')
    }
  }, [period, cursor, customFrom, customTo, tt])
  const { data: txs, isLoading } = useTransactions(range)
  const { data: accounts } = useAccounts()
  const { data: tags } = useTags()
  // Compras da fatura do mês por cartão (régua de fatura, TODAS as transações) — para
  // a linha-resumo do cartão bater com a tela do cartão.
  const { data: cardCompras } = useCardMonthCompras(cursor.getFullYear(), cursor.getMonth())
  // Compras do cartão pela régua de FATURA (statement_month) — usadas no modo MÊS
  // no lugar das compras por data, p/ cada compra aparecer na fatura (mês) a que
  // pertence, não no mês da data. Ver `effectiveTxs`.
  const { data: cardStatementTxs } = useCardMonthStatementTxs(cursor.getFullYear(), cursor.getMonth())
  // Saldo da FATURA do mês exibido, por cartão (≈0 = fatura paga). Régua da fatura
  // (byStatement), a mesma da tela do cartão — senão o badge diria "Paga" enquanto
  // a fatura ainda mostra saldo (ou vice-versa) por causa de compras pós-fechamento.
  const { data: faturaByCard } = useCardMonthFatura(cursor.getFullYear(), cursor.getMonth(), true)
  const del = useDeleteTransaction()
  const toggle = useToggleStatus()
  const confirm = useConfirm()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [transferOpen, setTransferOpen] = useState(false)
  const [editTransfer, setEditTransfer] = useState<TransferInfo | null>(null)
  const [includeOpen, setIncludeOpen] = useState(false)
  const [editing, setEditing] = useState<TransactionRow | null>(null)
  const [duplicating, setDuplicating] = useState<TransactionRow | null>(null)

  // Edição INLINE na lista (sem abrir o formulário). O lápis segue abrindo o form.
  const save = useSaveTransaction()
  const { data: categories } = useCategories()
  const [inlineEdit, setInlineEdit] = useState<{ id: string; field: 'description' | 'amount' | 'date' | 'category' } | null>(null)
  const [draft, setDraft] = useState('')
  function toInput(t: TransactionRow): TransactionInput {
    return {
      account_id: t.account_id,
      category_id: t.category_id,
      kind: t.kind,
      amount: Number(t.amount),
      currency: t.currency,
      date: t.date,
      description: t.description,
      status: t.status,
      exchange_rate: t.exchange_rate,
    }
  }
  function startEdit(t: TransactionRow, field: 'description' | 'amount' | 'date' | 'category') {
    setInlineEdit({ id: t.id, field })
    setDraft(field === 'amount' ? String(t.amount) : field === 'date' ? t.date : (t.description ?? ''))
  }
  async function saveInline(t: TransactionRow, patch: Partial<TransactionInput>) {
    setInlineEdit(null)
    try {
      await save.mutateAsync({ id: t.id, values: { ...toInput(t), ...patch } })
    } catch (e) {
      toast.error(tt('tx.errorSave'), { description: (e as Error).message })
    }
  }
  function commitText(t: TransactionRow) {
    if (!inlineEdit) return
    if (inlineEdit.field === 'description') void saveInline(t, { description: draft.trim() || null })
    else if (inlineEdit.field === 'amount') {
      const n = Number(draft)
      // Campo VAZIO = cancelar (não gravar 0). Digitar 0 explicitamente segue válido
      // (marca uma ocorrência recorrente como "não teve").
      if (draft.trim() !== '' && Number.isFinite(n) && n >= 0) void saveInline(t, { amount: n })
      else setInlineEdit(null)
    } else if (inlineEdit.field === 'date') {
      if (draft) void saveInline(t, { date: draft })
      else setInlineEdit(null)
    }
  }
  const isEditing = (id: string, field: string) => inlineEdit?.id === id && inlineEdit.field === field

  function shiftMonth(delta: number) {
    setCursor((c) => new Date(c.getFullYear(), c.getMonth() + delta, 1))
  }

  function openNew() {
    setEditing(null)
    setDuplicating(null)
    setDialogOpen(true)
  }
  function openEdit(t: TransactionRow) {
    setEditing(t)
    setDuplicating(null)
    setDialogOpen(true)
  }
  function openDuplicate(t: TransactionRow) {
    setEditing(null)
    setDuplicating(t)
    setDialogOpen(true)
  }
  async function remove(t: TransactionRow) {
    const isTransfer = Boolean(t.transfer_group_id)
    const ok = await confirm({
      title: isTransfer ? tt('tx.confirmDeleteTransferTitle') : tt('tx.confirmDeleteTitle'),
      description: isTransfer
        ? tt('tx.confirmDeleteTransferDesc')
        : tt('tx.confirmDeleteDesc'),
      confirmText: tt('common.delete'),
      tone: 'destructive',
    })
    if (!ok) return
    try {
      await del.mutateAsync({ id: t.id, transferGroupId: t.transfer_group_id })
      toast.success(isTransfer ? tt('tx.transferDeleted') : tt('tx.transactionDeleted'))
    } catch (e) {
      toast.error(tt('tx.errorDelete'), { description: (e as Error).message })
    }
  }
  // Exclui uma transferência colapsada (as duas pernas, pelo transfer_group_id).
  async function removeTransfer(tr: TransferInfo) {
    const ok = await confirm({
      title: tt('tx.confirmDeleteTransferTitle'),
      description: tt('tx.confirmDeleteTransferDesc2'),
      confirmText: tt('common.delete'),
      tone: 'destructive',
    })
    if (!ok) return
    try {
      await del.mutateAsync({ id: tr.legId, transferGroupId: tr.id })
      toast.success(tt('tx.transferDeleted'))
    } catch (e) {
      toast.error(tt('tx.errorDelete'), { description: (e as Error).message })
    }
  }
  async function onToggle(t: TransactionRow, checked: boolean) {
    try {
      await toggle.mutateAsync({ id: t.id, status: checked ? 'paid' : 'pending' })
      toast.success(checked ? tt('tx.markedPaid') : tt('tx.markedPending'))
    } catch (e) {
      toast.error(tt('tx.errorStatus'), { description: (e as Error).message })
    }
  }

  // Totais do mês (base), separando pago x pendente. Régua de CAIXA/FATURA
  // (faturaMonthOf), a mesma do dashboard e do Orçamento: uma compra de cartão
  // feita depois do fechamento cai na fatura do mês seguinte — não no caixa deste
  // mês, mesmo tendo data deste mês. Antes somava por data e divergia da fatura.
  // Cartões: as compras não aparecem individualmente — cada cartão vira UMA linha
  // com o total da fatura do mês (despesa − estorno). Clicar abre a fatura do cartão.
  const cardIds = useMemo(
    () => new Set((accounts ?? []).filter((a) => a.type === 'cartao').map((a) => a.id)),
    [accounts],
  )
  const cardName = useMemo(() => new Map((accounts ?? []).map((a) => [a.id, `${accountIcon(a)} ${a.name}`])), [accounts])
  const cardCurrency = useMemo(() => new Map((accounts ?? []).map((a) => [a.id, a.currency])), [accounts])
  // A data da linha do cartão é o VENCIMENTO da fatura. Como a fatura é nomeada
  // pelo mês em que vence, é o due_day do próprio mês exibido.
  const cardDueDay = useMemo(() => new Map((accounts ?? []).map((a) => [a.id, a.due_day])), [accounts])

  // FILTROS. A busca livre varre descrição, categoria, conta e tags (substring,
  // sem acento): "indus" acha lançamentos do "Banco Industrial".
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS)
  const hasFilter = activeCount(filters) > 0
  // Períodos maiores que um mês só com filtro ativo (senão puxaríamos um volume
  // enorme sem necessidade). Ao remover o filtro, volta automaticamente para "Mês".
  useEffect(() => {
    if (!hasFilter && period !== 'month') setPeriod('month')
  }, [hasFilter, period])
  // Filtrar pela categoria PAI precisa trazer os FILHOS junto.
  const catScope = useMemo(
    () => categoryScope(filters.categoryId, categories),
    [filters.categoryId, categories],
  )
  // Conjunto EFETIVO do período. No modo MÊS, as COMPRAS de cartão entram pela régua
  // de FATURA (statement_month) — não pela data: uma compra feita após o fechamento
  // aparece na fatura (mês) a que pertence, não no mês em que foi feita. Assim, ao
  // filtrar, o mês mostra só as compras DA fatura dele (não as da próxima). Pagamentos
  // (transferências) e contas normais seguem pela data. Em períodos multi-mês mantém a
  // régua de data (um intervalo abrange várias faturas de qualquer jeito).
  const effectiveTxs = useMemo(() => {
    const base = txs ?? []
    if (period !== 'month') return base
    const keep = base.filter((t) => t.transfer_group_id || !cardIds.has(t.account_id))
    return [...keep, ...(cardStatementTxs ?? [])]
  }, [txs, period, cardIds, cardStatementTxs])

  const filteredTxs = useMemo(() => {
    // Recorrente marcado como "não teve" vira uma transação valor 0 + tag 'não
    // aplicável' (só pra casar a ocorrência com a regra e sair do orçado). Isso
    // poluía a lista — some por padrão, mas reaparece se você filtrar por essa tag.
    const showSkips = filters.tag === 'não aplicável'
    return effectiveTxs.filter(
      (t) =>
        (showSkips || !(Number(t.amount) === 0 && (t.tags ?? []).includes('não aplicável'))) &&
        matchTx(t, filters, catScope),
    )
  }, [effectiveTxs, filters, catScope])

  // Total do que está VISÍVEL (após filtros) — em moeda base. Net = receitas −
  // despesas (transferências não contam). Útil p/ "total DOCAVISA no período".
  const periodTotal = useMemo(() => {
    let despesa = 0
    let receita = 0
    let count = 0
    const byCur = new Map<string, number>() // net (receita − despesa) por MOEDA NATIVA
    for (const t of filteredTxs) {
      if (t.transfer_group_id || t.kind === 'transfer') continue
      count++
      if (t.amount_base != null) {
        if (t.kind === 'expense') despesa += Number(t.amount_base)
        else receita += Number(t.amount_base)
      }
      const sign = t.kind === 'expense' ? -1 : 1
      byCur.set(t.currency, (byCur.get(t.currency) ?? 0) + sign * Number(t.amount))
    }
    return { despesa, receita, net: receita - despesa, count, byCur }
  }, [filteredTxs])
  // Totais na MOEDA NATIVA, só as moedas ≠ base (a base já é o "Total" principal).
  const nativeTotals = useMemo(
    () => [...periodTotal.byCur.entries()].filter(([c]) => c !== baseCurrency).sort((a, b) => a[0].localeCompare(b[0])),
    [periodTotal, baseCurrency],
  )

  // Exporta as transações FILTRADAS/do período como CSV (abre no Excel/Sheets).
  // Separador ';' e decimal ',' (pt-BR) + BOM para acentos. Sem lib externa.
  function exportCsv() {
    const esc = (v: string) => `"${String(v ?? '').replace(/"/g, '""')}"`
    const num = (n: number) => n.toFixed(2).replace('.', ',')
    // Nas categorias o emoji vive no NOME ("💵 Others Income") — tira pro CSV.
    const stripEmoji = (s: string) => s.replace(/^[\p{Extended_Pictographic}‍️\s]+/u, '').trim()
    const header = [tt('tx.colDate'), tt('tx.colDescription'), tt('tx.colCategory'), tt('tx.colAccount'), tt('tx.csvType'), tt('tx.csvCurrency'), tt('tx.colValue'), tt('tx.csvValueBase', { currency: baseCurrency }), tt('tx.csvStatus'), tt('tx.csvInstallment'), tt('tx.csvTags')]
    const rows = [...filteredTxs].sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0))
    const lines = [header.map(esc).join(';')]
    for (const t of rows) {
      const tipo = t.transfer_group_id ? tt('tx.transfer') : t.kind === 'income' ? tt('tx.income') : tt('tx.expense')
      const sign = t.kind === 'expense' ? -1 : 1
      const parcela = t.installment_total ? `${t.installment_no ?? '?'}/${t.installment_total}` : ''
      lines.push(
        [
          format(new Date(t.date + 'T00:00:00'), 'dd/MM/yyyy'),
          t.description ?? '',
          stripEmoji(t.category?.name ?? ''),
          t.account?.name ?? '',
          tipo,
          t.currency,
          num(sign * Number(t.amount)),
          t.amount_base != null ? num(sign * Number(t.amount_base)) : '',
          t.status === 'paid' ? tt('tx.statusPaid') : tt('tx.pending'),
          parcela,
          (t.tags ?? []).join(', '),
        ]
          .map(esc)
          .join(';'),
      )
    }
    const blob = new Blob(['﻿' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = range.from
      ? `${tt('tx.fileBase')}_${range.from}${range.to && range.to !== range.from ? '_' + tt('tx.fileTo') + '_' + range.to : ''}.csv`
      : `${tt('tx.fileAll')}.csv`
    a.click()
    URL.revokeObjectURL(url)
    toast.success(rows.length === 1 ? tt('tx.exportedOne') : tt('tx.exportedMany', { count: rows.length }))
  }

  const accountOptions: ComboOption[] = useMemo(
    () => accountComboOptions(accounts),
    [accounts],
  )
  const categoryOptions: ComboOption[] = useMemo(
    () =>
      (['expense', 'income', 'investment'] as const).flatMap((k) =>
        buildTree(categories ?? [], k).flatMap(({ parent, children }) => [
          { value: parent.id, label: tc(parent.name), bold: true } as ComboOption,
          ...children.map(
            (c): ComboOption => ({ value: c.id, label: tc(c.name), depth: 1, keywords: parent.name }),
          ),
        ]),
      ),
    [categories],
  )
  const tagOptions = useMemo(() => (tags ?? []).map((t) => t.name), [tags])

  // Todas as PERNAS de transferência do período, por grupo — para colapsar o par
  // numa linha só e mostrar o de/para (mesmo que o filtro case só uma perna).
  const transferGroups = useMemo(() => {
    const m = new Map<string, TransactionRow[]>()
    for (const t of txs ?? []) {
      if (!t.transfer_group_id) continue
      const arr = m.get(t.transfer_group_id) ?? []
      arr.push(t)
      m.set(t.transfer_group_id, arr)
    }
    return m
  }, [txs])

  const { nonCardTxs, cardGroups, transferRows } = useMemo(() => {
    // Separa as transferências (viram UMA linha por par) das transações normais.
    const transferRows: TransferInfo[] = []
    const seenGroup = new Set<string>()
    const regular: TransactionRow[] = []
    for (const t of filteredTxs) {
      if (t.transfer_group_id) {
        if (seenGroup.has(t.transfer_group_id)) continue
        seenGroup.add(t.transfer_group_id)
        const legs = transferGroups.get(t.transfer_group_id) ?? [t]
        const from = legs.find((l) => l.kind === 'expense') ?? null // saída = origem
        const to = legs.find((l) => l.kind === 'income') ?? null // entrada = destino
        const anchor = from ?? to ?? t
        transferRows.push({
          id: t.transfer_group_id,
          legId: anchor.id,
          date: anchor.date,
          description: anchor.description,
          status: anchor.status,
          fromAcc: from?.account ?? null,
          toAcc: to?.account ?? null,
          fromAccId: from?.account_id ?? null,
          toAccId: to?.account_id ?? null,
          fromAmount: from ? Number(from.amount) : Number(t.amount),
          fromCurrency: from?.currency ?? t.currency,
          fromBase: from?.amount_base != null ? Number(from.amount_base) : null,
          toAmount: to ? Number(to.amount) : Number(t.amount),
          toCurrency: to?.currency ?? t.currency,
        })
        continue
      }
      regular.push(t)
    }

    // Com filtro ativo OU período multi-mês, o cartão NÃO vira resumo de fatura: as
    // compras aparecem uma a uma (a régua de fatura só faz sentido num mês só, e um
    // gasto que casa com o filtro não pode ficar escondido dentro da fatura).
    if (hasFilter || period !== 'month')
      return { nonCardTxs: regular, cardGroups: [] as { accountId: string; native: number; base: number }[], transferRows }
    // As transações de cartão saem da LISTA e viram um resumo de fatura. O VALOR do
    // resumo vem de `useCardMonthCompras` (régua de fatura sobre TODAS as transações
    // do cartão) — não da soma por data aqui, que subcontaria as compras datadas em
    // outro mês mas com statement_month neste (empurradas pelo fechamento).
    const nonCardTxs = regular.filter((t) => !cardIds.has(t.account_id))
    const cardGroups = [...(cardCompras ?? new Map<string, { native: number; base: number }>()).entries()]
      .filter(([, v]) => Math.abs(v.native) > 0.005 || Math.abs(v.base) > 0.005)
      .map(([accountId, v]) => ({ accountId, native: v.native, base: v.base }))
      .sort((a, b) => (cardName.get(a.accountId) ?? '').localeCompare(cardName.get(b.accountId) ?? ''))
    return { nonCardTxs, cardGroups, transferRows }
  }, [filteredTxs, transferGroups, hasFilter, period, cardIds, cardName, range, cardCompras])

  const monthKey = range.from ? range.from.slice(0, 7) : ''
  const hasRows = cardGroups.length > 0 || nonCardTxs.length > 0

  // Ordenação por coluna (inclui as linhas de fatura de cartão).
  type SortKey = 'status' | 'date' | 'description' | 'category' | 'account' | 'value'
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' }>({ key: 'date', dir: 'desc' })
  function toggleSort(key: SortKey) {
    setSort((s) =>
      s.key === key
        ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: key === 'value' || key === 'date' ? 'desc' : 'asc' },
    )
  }

  type URow =
    | { type: 'card'; accountId: string; native: number; base: number }
    | { type: 'tx'; tx: TransactionRow }
    | { type: 'transfer'; tr: TransferInfo }
  const sortedRows = useMemo(() => {
    const rows: URow[] = [
      ...cardGroups.map((g) => ({ type: 'card' as const, accountId: g.accountId, native: g.native, base: g.base })),
      ...nonCardTxs.map((tx) => ({ type: 'tx' as const, tx })),
      ...transferRows.map((tr) => ({ type: 'transfer' as const, tr })),
    ]
    const y = cursor.getFullYear()
    const m = cursor.getMonth()
    const val = (r: URow): string | number => {
      if (r.type === 'card') {
        const name = cardName.get(r.accountId) ?? ''
        switch (sort.key) {
          case 'date':
            return dueISOForMonth(cardDueDay.get(r.accountId), y, m) ?? ''
          case 'description':
          case 'account':
            return name
          case 'value':
            return -r.base
          default:
            return ''
        }
      }
      if (r.type === 'transfer') {
        const tr = r.tr
        switch (sort.key) {
          case 'date':
            return tr.date
          case 'description':
            return tr.description ?? 'Transferência'
          case 'account':
            return tr.fromAcc?.name ?? ''
          case 'value':
            return tr.fromBase ?? 0
          case 'status':
            return tr.status
          default:
            return ''
        }
      }
      const t = r.tx
      switch (sort.key) {
        case 'date':
          return t.date
        case 'description':
          return t.description ?? ''
        case 'category':
          return t.category?.name ?? ''
        case 'account':
          return t.account?.name ?? ''
        case 'value':
          return t.amount_base ?? 0
        case 'status':
          return t.status
      }
    }
    const dir = sort.dir === 'asc' ? 1 : -1
    return rows.slice().sort((a, b) => {
      const va = val(a)
      const vb = val(b)
      const c =
        typeof va === 'number' && typeof vb === 'number'
          ? va - vb
          : String(va).localeCompare(String(vb), 'pt-BR')
      return c * dir
    })
  }, [cardGroups, nonCardTxs, transferRows, sort, cursor, cardName, cardDueDay])

  function sortHead(label: string, key: SortKey, className?: string) {
    const active = sort.key === key
    const Icon = active ? (sort.dir === 'asc' ? ArrowUp : ArrowDown) : ChevronsUpDown
    return (
      <TableHead className={cn('hover:text-foreground cursor-pointer select-none', className)} onClick={() => toggleSort(key)}>
        <span className="inline-flex items-center gap-1">
          {label}
          <Icon className={cn('size-3', active ? 'opacity-80' : 'opacity-30')} />
        </span>
      </TableHead>
    )
  }

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <PageHeader
        title={tt('tx.title')}
        subtitle={tt('tx.subtitle', { currency: baseCurrency })}
        actions={
          <>
            <Button variant="outline" onClick={exportCsv} disabled={filteredTxs.length === 0} title={tt('tx.exportTitle')}>
              <Download className="size-4" /> {tt('tx.export')}
            </Button>
            <Button variant="outline" onClick={() => setIncludeOpen(true)}>
              <Repeat className="size-4" /> {tt('tx.includeRecurring')}
            </Button>
            <Button variant="outline" onClick={() => setTransferOpen(true)}>
              <ArrowLeftRight className="size-4" /> {tt('tx.transfer')}
            </Button>
            <Button onClick={openNew}>
              <Plus className="size-4" /> {tt('tx.new')}
            </Button>
          </>
        }
      />

      {/* Período: mês navegável (padrão) ou faixas maiores + total do filtrado */}
      <div className="flex flex-wrap items-center gap-2 rounded-lg border p-2">
        {/* Esquerda: seletor de período */}
        <Select value={period} onValueChange={(v) => setPeriod(v as PeriodMode)}>
          <SelectTrigger
            className="h-8 w-[150px] shrink-0"
            title={hasFilter ? undefined : tt('tx.periodHint')}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="month">{tt('tx.month')}</SelectItem>
            <SelectItem value="3m" disabled={!hasFilter}>{tt('tx.last3')}</SelectItem>
            <SelectItem value="6m" disabled={!hasFilter}>{tt('tx.last6')}</SelectItem>
            <SelectItem value="12m" disabled={!hasFilter}>{tt('tx.last12')}</SelectItem>
            <SelectItem value="year" disabled={!hasFilter}>{tt('tx.year', { year: new Date().getFullYear() })}</SelectItem>
            <SelectItem value="all" disabled={!hasFilter}>{tt('tx.all')}</SelectItem>
            <SelectItem value="custom" disabled={!hasFilter}>{tt('tx.custom')}</SelectItem>
          </SelectContent>
        </Select>

        {/* Centro: navegador de mês / intervalo / rótulo do período */}
        <div className="flex flex-1 items-center justify-center gap-1">
          {period === 'month' ? (
            <>
              <Button variant="ghost" size="icon" onClick={() => shiftMonth(-1)} aria-label={tt('tx.prevMonth')}>
                <ChevronLeft className="size-5" />
              </Button>
              <div className="flex flex-col items-center">
                <MonthPicker value={cursor} onChange={setCursor} />
                {!isCurrentMonth(cursor) && (
                  <button
                    className="text-primary mt-0.5 text-xs hover:underline"
                    onClick={() => setCursor(new Date())}
                  >
                    {tt('acct.backToCurrentMonth')}
                  </button>
                )}
              </div>
              <Button variant="ghost" size="icon" onClick={() => shiftMonth(1)} aria-label={tt('tx.nextMonth')}>
                <ChevronRight className="size-5" />
              </Button>
            </>
          ) : period === 'custom' ? (
            <div className="flex items-center gap-1.5 text-sm">
              <DateInput value={customFrom} onChange={setCustomFrom} className="h-8 w-[132px]" />
              <span className="text-muted-foreground">{tt('tx.until')}</span>
              <DateInput value={customTo} onChange={setCustomTo} className="h-8 w-[132px]" />
            </div>
          ) : (
            <span className="text-sm font-medium capitalize">{periodLabel}</span>
          )}
        </div>

        {/* Direita: total do filtrado */}
        <div className="text-muted-foreground shrink-0 text-right text-xs">
          <div>
            <b className="text-foreground tabular-nums">{periodTotal.count}</b> {tt('tx.entriesShort')} · {tt('tx.total')}{' '}
            <span className={`tabular-nums ${periodTotal.net < 0 ? 'text-destructive' : 'text-success'}`}>
              {formatCurrency(periodTotal.net, baseCurrency)}
            </span>
          </div>
          {hasFilter && nativeTotals.length > 0 && (
            <div className="tabular-nums">
              {nativeTotals.map(([cur, v], i) => (
                <span key={cur}>
                  {i > 0 && <span className="text-muted-foreground"> · </span>}
                  <span className={v < 0 ? 'text-destructive' : 'text-success'}>{formatCurrency(v, cur)}</span>
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      <FilterBar
        filters={filters}
        onChange={setFilters}
        accounts={accountOptions}
        categories={categoryOptions}
        tags={tagOptions}
        placeholder={tt('tx.searchPlaceholder')}
        extraActive={period !== 'month'}
        onClear={() => {
          setPeriod('month')
          setCustomFrom('')
          setCustomTo('')
        }}
      />

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <TableSkeleton rows={8} cols={7} />
          ) : hasRows ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    {sortHead('✓', 'status', 'w-10')}
                    {sortHead(tt('tx.colDate'), 'date')}
                    {sortHead(tt('tx.colDescription'), 'description')}
                    {sortHead(tt('tx.colCategory'), 'category')}
                    {sortHead(tt('tx.colAccount'), 'account')}
                    {sortHead(tt('tx.colValue'), 'value', 'text-right')}
                    <TableHead className="w-20" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedRows.map((r) =>
                    r.type === 'card' ? (
                    <TableRow
                      key={'card-' + r.accountId}
                      className="bg-muted/30 hover:bg-muted/60 cursor-pointer"
                      onClick={() => navigate(`/cartoes/${r.accountId}?m=${monthKey}`)}
                    >
                      <TableCell>
                        {/* Check read-only: reflete se a fatura está paga (saldo devedor ~0).
                            Muda sozinho conforme a fatura é quitada — não é editável aqui. */}
                        <Checkbox
                          checked={
                            !!(faturaByCard?.has(r.accountId) && (faturaByCard.get(r.accountId) as number) < 0.005)
                          }
                          disabled
                          aria-label={tt('tx.invoicePaid')}
                          onClick={(e) => e.stopPropagation()}
                          className="opacity-80 data-[state=checked]:bg-green-700 data-[state=checked]:border-green-700 data-[state=checked]:text-white dark:data-[state=checked]:bg-green-800 dark:data-[state=checked]:border-green-800"
                        />
                      </TableCell>
                      <TableCell className="whitespace-nowrap tabular-nums">
                        {dueDateForMonth(cardDueDay.get(r.accountId), cursor.getFullYear(), cursor.getMonth()) ?? (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell colSpan={3} className="font-medium">
                        <span className="inline-flex items-center gap-1.5">
                          {cardName.get(r.accountId) ?? tt('tx.card')}
                        </span>
                      </TableCell>
                      <TableCell className="text-right tabular-nums whitespace-nowrap">
                        {r.native > 0.005 ? (
                          <span className="inline-flex items-center justify-end gap-1">
                            -{formatCurrency(r.native, cardCurrency.get(r.accountId) ?? baseCurrency)}
                            <ConversionIndicator
                              amount={r.native}
                              currency={cardCurrency.get(r.accountId) ?? baseCurrency}
                              baseCurrency={baseCurrency}
                              converted={r.base}
                            />
                          </span>
                        ) : (
                          ''
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <ChevronRight className="text-muted-foreground ml-auto size-4" />
                      </TableCell>
                    </TableRow>
                    ) : r.type === 'transfer' ? (
                    <TableRow key={'tr-' + r.tr.id}>
                      <TableCell>
                        <Checkbox
                          checked
                          disabled
                          aria-label={tt('tx.transferSettled')}
                          className="opacity-80 data-[state=checked]:bg-green-700 data-[state=checked]:border-green-700 data-[state=checked]:text-white dark:data-[state=checked]:bg-green-800 dark:data-[state=checked]:border-green-800"
                        />
                      </TableCell>
                      <TableCell className="whitespace-nowrap tabular-nums">
                        {format(new Date(r.tr.date + 'T00:00:00'), 'dd/MM/yyyy')}
                      </TableCell>
                      <TableCell className="max-w-[220px]">
                        <div className="flex items-center gap-1.5">
                          <ArrowLeftRight className="text-muted-foreground size-3 shrink-0" />
                          <span className="truncate">{r.tr.description || tt('tx.transfer')}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground">—</TableCell>
                      <TableCell
                        className="max-w-[240px] whitespace-nowrap"
                        title={tt('tx.fromTo', { from: r.tr.fromAcc?.name ?? '—', to: r.tr.toAcc?.name ?? '—' })}
                      >
                        <span className="flex items-center gap-1 truncate">
                          <span className="truncate">
                            {r.tr.fromAcc ? `${accountIcon(r.tr.fromAcc)} ${r.tr.fromAcc.name}` : '—'}
                          </span>
                          <ArrowRight className="text-muted-foreground size-3 shrink-0" />
                          <span className="truncate">
                            {r.tr.toAcc ? `${accountIcon(r.tr.toAcc)} ${r.tr.toAcc.name}` : '—'}
                          </span>
                        </span>
                      </TableCell>
                      <TableCell className="text-right tabular-nums whitespace-nowrap">
                        <span className="inline-flex items-center justify-end gap-1">
                          <span>{formatCurrency(r.tr.fromAmount, r.tr.fromCurrency)}</span>
                          {r.tr.fromCurrency !== baseCurrency && r.tr.fromBase != null && (
                            <ConversionIndicator
                              amount={r.tr.fromAmount}
                              currency={r.tr.fromCurrency}
                              baseCurrency={baseCurrency}
                              converted={r.tr.fromBase}
                            />
                          )}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setEditTransfer(r.tr)}
                            aria-label={tt('common.edit')}
                          >
                            <Pencil className="size-4" />
                          </Button>
                          {/* Transferência não tem "duplicar" — placeholder p/ alinhar os ícones. */}
                          <span
                            aria-hidden
                            className="text-muted-foreground/30 inline-flex size-9 items-center justify-center text-sm select-none"
                          >
                            —
                          </span>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => removeTransfer(r.tr)}
                            aria-label={tt('tx.deleteTransfer')}
                          >
                            <Trash2 className="text-destructive size-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                    ) : (
                    (() => { const t = r.tx; return (
                    <TableRow key={t.id} className={t.status === 'pending' ? 'opacity-70' : ''}>
                      <TableCell>
                        <Checkbox
                          checked={t.status === 'paid'}
                          onCheckedChange={(v) => onToggle(t, v === true)}
                          aria-label={t.status === 'paid' ? tt('tx.markPending') : tt('tx.markPaid')}
                          // "Pago" em cinza sutil (o verde em toda linha virava uma "parede").
                          className="data-[state=checked]:bg-green-700 data-[state=checked]:border-green-700 data-[state=checked]:text-white dark:data-[state=checked]:bg-green-800 dark:data-[state=checked]:border-green-800"
                        />
                      </TableCell>
                      <TableCell className="whitespace-nowrap tabular-nums">
                        {isEditing(t.id, 'date') ? (
                          <DateInput
                            value={draft}
                            onChange={(iso) => iso && void saveInline(t, { date: iso })}
                            onBlur={() => setInlineEdit(null)}
                            onKeyDown={(e) => {
                              if (e.key === 'Escape') setInlineEdit(null)
                            }}
                          />
                        ) : (
                          <span className="cursor-pointer hover:underline" onClick={() => startEdit(t, 'date')}>
                            {format(new Date(t.date + 'T00:00:00'), 'dd/MM/yyyy')}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="max-w-[220px]">
                        {isEditing(t.id, 'description') ? (
                          <Input
                            autoFocus
                            value={draft}
                            onChange={(e) => setDraft(e.target.value)}
                            onBlur={() => commitText(t)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') commitText(t)
                              if (e.key === 'Escape') setInlineEdit(null)
                            }}
                            className="h-8"
                          />
                        ) : (
                          <div className="flex items-center gap-1.5">
                            {t.transfer_group_id && <ArrowLeftRight className="text-muted-foreground size-3 shrink-0" />}
                            {t.recurring_id && <Repeat className="text-muted-foreground size-3 shrink-0" />}
                            <span
                              className="max-w-[170px] cursor-pointer truncate hover:underline"
                              onClick={() => startEdit(t, 'description')}
                            >
                              {t.description || <span className="text-muted-foreground">—</span>}
                            </span>
                            <span className="flex shrink-0 items-center gap-1">
                              {t.status === 'pending' && <Badge variant="outline">{tt('tx.pending')}</Badge>}
                              <TagBadges tags={t.tags} />
                            </span>
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        {isEditing(t.id, 'category') ? (
                          <Combobox
                            autoOpen
                            className="h-8"
                            value={t.category_id ?? 'none'}
                            onChange={(v) => void saveInline(t, { category_id: v === 'none' ? null : v })}
                            onClose={() => setInlineEdit(null)}
                            searchPlaceholder={tt('dlg.search')}
                            aria-label={tt('tx.colCategory')}
                            options={[
                              { value: 'none', label: tt('tx.noCategory') },
                              ...buildPicker(categories ?? [], t.kind === 'income' ? 'income' : 'expense').flatMap(
                                ({ parent, children }) => [
                                  { value: parent.id, label: tc(parent.name), bold: true } as ComboOption,
                                  ...children.map(
                                    (c): ComboOption => ({ value: c.id, label: tc(c.name), depth: 1, keywords: parent.name }),
                                  ),
                                ],
                              ),
                            ]}
                          />
                        ) : (
                          <span className="cursor-pointer hover:underline" onClick={() => startEdit(t, 'category')}>
                            {tc(t.category?.name) || '—'}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        {t.account ? `${accountIcon(t.account)} ${t.account.name}` : '—'}
                      </TableCell>
                      <TableCell
                        className={`text-right tabular-nums whitespace-nowrap ${
                          t.kind === 'income' ? 'text-success' : ''
                        }`}
                      >
                        {isEditing(t.id, 'amount') ? (
                          <MoneyInput
                            autoFocus
                            value={draft ? Number(draft) : 0}
                            onChange={(n) => setDraft(n ? String(n) : '')}
                            onBlur={() => commitText(t)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') commitText(t)
                              if (e.key === 'Escape') setInlineEdit(null)
                            }}
                            className="ml-auto h-8 w-24 text-right"
                          />
                        ) : (
                          <span className="inline-flex items-center justify-end gap-1">
                            <span className="cursor-pointer hover:underline" onClick={() => startEdit(t, 'amount')}>
                              {t.kind === 'expense' ? '-' : '+'}
                              {formatCurrency(Number(t.amount), t.currency)}
                            </span>
                            {t.currency !== baseCurrency &&
                              (t.amount_base != null ? (
                                <ConversionIndicator
                                  amount={Number(t.amount)}
                                  currency={t.currency}
                                  baseCurrency={baseCurrency}
                                  converted={Number(t.amount_base)}
                                  rate={t.exchange_rate}
                                />
                              ) : (
                                <AlertCircle className="text-muted-foreground size-3" aria-label={tt('tx.conversionPending')} />
                              ))}
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="icon" onClick={() => openEdit(t)} aria-label={tt('common.edit')}>
                            <Pencil className="size-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => openDuplicate(t)} aria-label={tt('tx.duplicate')}>
                            <Copy className="size-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => remove(t)} aria-label={tt('common.delete')}>
                            <Trash2 className="text-destructive size-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                    )
                    })()
                    )
                  )}
                </TableBody>
              </Table>
            </div>
          ) : (
            <EmptyState
              icon={ArrowLeftRight}
              title={tt('tx.empty', { periodo: periodLabel })}
              description={tt('tx.emptyDesc')}
              action={
                <Button onClick={openNew}>
                  <Plus className="size-4" /> {tt('tx.new')}
                </Button>
              }
            />
          )}
        </CardContent>
      </Card>

      <TransactionFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        transaction={editing}
        duplicateFrom={duplicating}
        defaultDate={defaultDateForMonth(cursor)}
      />
      <TransferDialog open={transferOpen} onOpenChange={setTransferOpen} />

      {/* Edição de uma transferência existente (recria o par no mesmo grupo). */}
      <TransferDialog
        open={editTransfer != null}
        onOpenChange={(v) => !v && setEditTransfer(null)}
        title={tt('dlg.tr.editTitle')}
        editGroupId={editTransfer?.id}
        defaultFromId={editTransfer?.fromAccId ?? undefined}
        defaultToId={editTransfer?.toAccId ?? undefined}
        defaultAmount={editTransfer ? String(editTransfer.fromAmount) : undefined}
        defaultToAmount={editTransfer ? String(editTransfer.toAmount) : undefined}
        defaultDate={editTransfer?.date}
        defaultDescription={
          editTransfer && editTransfer.description !== 'Transferência' ? (editTransfer.description ?? '') : ''
        }
      />
      <IncludeRecurringDialog
        open={includeOpen}
        onOpenChange={setIncludeOpen}
        year={cursor.getFullYear()}
        month0={cursor.getMonth()}
      />
    </div>
  )
}
